import { useRef, useState, type ReactNode } from "react";
import type { ScopeLayer } from "../../api/statementConfig.ts";
import { keyLabel } from "./keyLabels.ts";
import s from "./statementConfig.module.scss";

export const Icons = {
  chevDown: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14" aria-hidden="true"><path d="m4 6 4 4 4-4" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  plus:     <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14" aria-hidden="true"><path d="M8 3v10M3 8h10" strokeLinecap="round"/></svg>,
  x:        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14" aria-hidden="true"><path d="m4 4 8 8M12 4l-8 8" strokeLinecap="round"/></svg>,
  cal:      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14" aria-hidden="true"><rect x="2.5" y="3.5" width="11" height="10" rx="1.5"/><path d="M2.5 6.5h11M5.5 2v3M10.5 2v3" strokeLinecap="round"/></svg>,
  info:     <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14" aria-hidden="true"><circle cx="8" cy="8" r="6"/><path d="M8 7v4M8 5v.01" strokeLinecap="round"/></svg>,
  warn:     <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14" aria-hidden="true"><path d="M8 2 1.5 13.5h13L8 2Z" strokeLinejoin="round"/><path d="M8 6.5v3.5M8 12v.01" strokeLinecap="round"/></svg>,
  check:    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14" aria-hidden="true"><path d="m3 8.5 3.5 3 6.5-7" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  refresh:  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14" aria-hidden="true"><path d="M2.5 8a5.5 5.5 0 1 1 1.6 3.9M2.5 13V11h2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  save:     <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14" aria-hidden="true"><path d="M3 3h8.5L13 4.5V13H3V3Z" strokeLinejoin="round"/><path d="M5 3v3.5h6V3M5 13v-4h6v4"/></svg>,
  copy:     <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14" aria-hidden="true"><rect x="5" y="5" width="9" height="9" rx="1.5"/><path d="M11 5V3.5a1 1 0 0 0-1-1H3.5a1 1 0 0 0-1 1V10a1 1 0 0 0 1 1H5"/></svg>,
  arrow:    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  back:     <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14" aria-hidden="true"><path d="M13 8H3M7 4 3 8l4 4" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  grip:     <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14" aria-hidden="true"><circle cx="6" cy="4" r="1"/><circle cx="6" cy="8" r="1"/><circle cx="6" cy="12" r="1"/><circle cx="10" cy="4" r="1"/><circle cx="10" cy="8" r="1"/><circle cx="10" cy="12" r="1"/></svg>,
  scale:    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14" aria-hidden="true"><path d="M8 2v12M2 4h12M2 14h12M5 4l-3 5h6L5 4ZM11 4l3 5h-6l3-5Z"/></svg>,
  ext:      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14" aria-hidden="true"><path d="M9 3h4v4M7 9l6-6M12.5 9.5V13H3V3.5H7"/></svg>,
  search:   <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14" aria-hidden="true"><circle cx="7" cy="7" r="4.5"/><path d="m10.5 10.5 3 3" strokeLinecap="round"/></svg>,
  kebab:    <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14" aria-hidden="true"><circle cx="8" cy="3.5" r="1.25"/><circle cx="8" cy="8" r="1.25"/><circle cx="8" cy="12.5" r="1.25"/></svg>,
};

export function Kbd({ children }: { children: ReactNode }) {
  return <span className={s.kbd}>{children}</span>;
}

export function Tag({ children, variant, dot }: {
  children: ReactNode;
  variant?: "solid" | "danger" | "success" | "warning" | "info";
  dot?: boolean;
}) {
  const cls = [s.tag];
  if (variant === "solid")   cls.push(s.tagSolid);
  if (variant === "danger")  cls.push(s.tagDanger);
  if (variant === "success") cls.push(s.tagSuccess);
  if (variant === "warning") cls.push(s.tagWarning);
  if (variant === "info")    cls.push(s.tagInfo);
  return (
    <span className={cls.join(" ")}>
      {dot && <span className={s.tagDot} />}
      {children}
    </span>
  );
}

export function LayerBadge({ layer, dim }: { layer: ScopeLayer; dim?: boolean }) {
  const cls = [s.layer];
  if (layer === "DATABASE") cls.push(s.layerDb);
  if (layer === "OPERATOR") cls.push(s.layerOp);
  if (layer === "KIOSK")    cls.push(s.layerKk);
  if (layer === "RESELLER") cls.push(s.layerReseller);
  if (layer === "RESELLER_INBOUND") cls.push(s.layerResellerInbound);
  if (dim) cls.push(s.layerDim);
  return <span className={cls.join(" ")}>{layer}</span>;
}

export interface SegOption<T extends string> { value: T; label: string; }
export function Segmented<T extends string>({ value, options, onChange, size, disabled }: {
  value: T; options: SegOption<T>[];
  onChange?: (v: T) => void;
  size?: "sm";
  disabled?: boolean;
}) {
  const cls = [s.segmented];
  if (size === "sm") cls.push(s.segmentedSm);
  return (
    <div className={cls.join(" ")} role="group">
      {options.map(o => (
        <button
          key={o.value} type="button"
          className={`${s.segmentedBtn}${o.value === value ? " " + s.segmentedBtnOn : ""}`}
          onClick={() => !disabled && onChange?.(o.value)}
          aria-pressed={o.value === value}
          disabled={disabled}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function InhOverToggle({ value, onChange }: {
  value: "inherit" | "override";
  onChange?: (v: "inherit" | "override") => void;
}) {
  return (
    <Segmented<"inherit" | "override">
      size="sm" value={value} onChange={onChange}
      options={[{ value: "inherit", label: "Inherit" }, { value: "override", label: "Override" }]}
    />
  );
}

export function Banner({ variant = "info", icon, children }: {
  variant?: "info" | "warning" | "error" | "success";
  icon?: ReactNode;
  children: ReactNode;
}) {
  const cls = [s.banner];
  if (variant === "info")    cls.push(s.bannerInfo);
  if (variant === "warning") cls.push(s.bannerWarning);
  if (variant === "error")   cls.push(s.bannerError);
  if (variant === "success") cls.push(s.bannerSuccess);
  const fallbackIcon = variant === "warning" || variant === "error" ? Icons.warn : Icons.info;
  return (
    <div className={cls.join(" ")}>
      <span className={s.bannerIcon}>{icon ?? fallbackIcon}</span>
      <div className={s.bannerBody}>{children}</div>
    </div>
  );
}

export function KeyLabel({ k, mono }: { k: string; mono?: boolean }) {
  const label = keyLabel(k);
  const showRaw = label !== k;
  return (
    <span
      title={showRaw ? `Backend key: ${k}` : k}
      style={{
        fontFamily: mono ? "var(--font-family-mono)" : "var(--font-family-sans)",
        cursor: showRaw ? "help" : "default",
        borderBottom: showRaw ? "1px dotted var(--color-border-hover)" : "none",
      }}
    >
      {label}
    </span>
  );
}

export function Input({
  value, placeholder, width, disabled, mono, suffix, prefix, icon, sm, readOnly, onChange, type = "text",
}: {
  value?: string | number;
  placeholder?: string;
  width?: number | string;
  disabled?: boolean;
  mono?: boolean;
  suffix?: ReactNode;
  prefix?: ReactNode;
  icon?: ReactNode;
  sm?: boolean;
  readOnly?: boolean;
  onChange?: (v: string) => void;
  type?: string;
}) {
  const cls = [s.input];
  if (disabled) cls.push(s.inputDisabled);
  if (mono) cls.push(s.inputMono);
  if (sm) cls.push(s.inputSm);
  return (
    <label className={cls.join(" ")} style={{ width }}>
      {icon && <span className={s.inputIcon}>{icon}</span>}
      {prefix && <span className={s.inputAffix}>{prefix}</span>}
      <input
        value={value ?? ""} placeholder={placeholder} disabled={disabled}
        readOnly={readOnly} type={type}
        onChange={(e) => onChange?.(e.target.value)}
      />
      {suffix && <span className={s.inputAffix}>{suffix}</span>}
    </label>
  );
}

/** Whether the text in a numeric field still means `value` - by the loosest rule the callers parse
 *  with (numOrZero strips anything not numeric, an empty field is unset or 0). */
export function textMeans(text: string, value: number | null | undefined): boolean {
  const t = text.trim();
  if (t === "") return value == null || value === 0;
  return Number(t.replace(",", ".").replace(/[^0-9.\-]/g, "")) === value;
}

/** A numeric Input that keeps the text being typed ("1742.", "0.0") while it still means the value.
 *  The plain Input is controlled by the parsed number, so a decimal point vanished the moment it was
 *  typed, and "0.5" could not be entered at all. Same onChange as Input: the caller parses. */
export function DecimalInput({ value, onChange, ...rest }: Omit<Parameters<typeof Input>[0], "value" | "onChange"> & {
  value: number | null | undefined;
  onChange: (v: string) => void;
}) {
  const [text, setText] = useState(value == null ? "" : String(value));
  return (
    <Input {...rest} value={textMeans(text, value) ? text : value == null ? "" : String(value)}
      onChange={(v) => { setText(v); onChange(v); }} />
  );
}

export function Select<T extends string>({
  value, options, width, disabled, sm, onChange,
}: {
  value: T;
  options: T[] | { value: T; label: string }[];
  width?: number | string;
  disabled?: boolean;
  sm?: boolean;
  onChange?: (v: T) => void;
}) {
  const cls = [s.input];
  if (disabled) cls.push(s.inputDisabled);
  if (sm) cls.push(s.inputSm);
  const opts: { value: T; label: string }[] = options.map(o =>
    typeof o === "string" ? { value: o as T, label: o } : o,
  );
  const ref = useRef<HTMLSelectElement>(null);
  // Native <select> opens on its own click — don't bind anything on the label
  // or any ancestor, otherwise the option click bubbles back and re-triggers
  // showPicker(), reopening the dropdown after the user picks a value.
  //
  // The chevron icon is the only piece a user might click *outside* the
  // <select>'s own hit-area, so we forward that single click into the select
  // via mousedown (preventDefault stops focus from landing on the icon span
  // first, which would otherwise eat the open).
  const openOnChevron = (e: React.MouseEvent) => {
    if (disabled) return;
    e.preventDefault();
    const el = ref.current;
    if (!el) return;
    if (typeof el.showPicker === "function") {
      try { el.showPicker(); return; } catch { /* fall through */ }
    }
    el.focus();
  };
  return (
    <label className={cls.join(" ")} style={{ width }}>
      <select
        ref={ref}
        value={value} disabled={disabled}
        onChange={(e) => onChange?.(e.target.value as T)}
      >
        {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <span className={s.inputIcon} onMouseDown={openOnChevron}>{Icons.chevDown}</span>
    </label>
  );
}

export function FieldRow({
  label, required, control, inh, onInhChange, parentHint, hint, error, tip,
}: {
  label: string;
  required?: boolean;
  control: ReactNode;
  inh?: "inherit" | "override" | null;
  onInhChange?: (v: "inherit" | "override") => void;
  parentHint?: string;
  hint?: ReactNode;
  error?: string;
  tip?: string;
}) {
  return (
    <div className={s.fieldRow}>
      <div className={s.fieldLabel}>
        <span>
          {label}
          {required && <span className={s.fieldRequired}> *</span>}
        </span>
        {tip && (
          <span className={s.fieldTipTrigger} title={tip} aria-label={tip}>
            {Icons.info}
          </span>
        )}
      </div>
      <div className={s.fieldControl}>
        <div className={inh === "inherit" ? s.fieldControlInherit : ""}>{control}</div>
        {inh === "inherit" && parentHint && (
          <div className={s.fieldHintInherit}>
            ↳ inheriting <code>{parentHint}</code> from parent layer
          </div>
        )}
        {hint && !error && <div className={s.fieldHint}>{hint}</div>}
        {error && <div className={s.fieldError}>{error}</div>}
      </div>
      <div className={s.fieldInhSlot}>
        {inh ? <InhOverToggle value={inh} onChange={onInhChange} /> : null}
      </div>
    </div>
  );
}

export function FormSection({ title, sub, right, children }: {
  title: string; sub?: string; right?: ReactNode; children: ReactNode;
}) {
  return (
    <section className={s.section}>
      <div className={s.sectionHead}>
        <div>
          <div className={s.sectionTitle}>{title}</div>
          {sub && <div className={s.sectionSub}>{sub}</div>}
        </div>
        {right}
      </div>
      <div>{children}</div>
    </section>
  );
}

export const styles = s;

/** Parse a free-form numeric input — strips non-numeric chars then `Number()`.
 *  Returns 0 on empty / non-numeric. Used by every money/percentage Input in
 *  the statement-config form, shared so the parsing rule stays consistent. */
export function numOrZero(v: string): number {
  const n = Number(v.replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}
